import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Vehicle } from '@hero/schema';
import { useVehicleListApi, useVehicleApi } from '@hero/api-hooks';
import { getVehicleBrandLabel } from '@hero/hooks-shared';
import { SelectAsync, type SelectionValue, type SelectAsyncMode } from '../../../app/Select';
import { VehicleEditModal } from '../Modal/VehicleEditModal';
import { VehicleDynamicIcon, UserIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import {
  AvailabilityChip,
  formatPlateForDisplay,
  vehicleStatusChip,
  SelectRowCheck,
} from '../../../app/Select/SelectAsync/selectorEntityRowUtils';

export type VehicleSelectionValue = SelectionValue<Vehicle>;

export interface VehicleSelectorProps {
  selectedVehicle?: VehicleSelectionValue;
  onVehicleSelected: (vehicles: Vehicle[] | undefined) => void;
  onVehicleSave?: (vehicle: Vehicle) => void;
  allowCreateNew?: boolean;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  required?: boolean;
  mode?: SelectAsyncMode;
}

export const VehicleSelector = React.memo(function VehicleSelector({
  selectedVehicle,
  onVehicleSelected,
  onVehicleSave,
  allowCreateNew: allowCreateNewProp,
  disabled = false,
  placeholder,
  label,
  required,
  mode = 'single',
}: VehicleSelectorProps) {
  const { t, i18n } = useTranslation(['m-vehicle', 'c-main']);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const dir = i18n.dir();

  const vehicleListQuery = useVehicleListApi({});
  const vehicleSingleQuery = useVehicleApi(undefined);

  const allowCreateNew = allowCreateNewProp !== false;

  const openCreateModal = useCallback(() => {
    setIsCreateModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsCreateModalOpen(false);
  }, []);

  const handleVehicleSave = useCallback(
    (vehicle: Vehicle) => {
      onVehicleSave?.(vehicle);
      closeModal();
    },
    [onVehicleSave, closeModal],
  );

  const renderVehicleRow = useMemo(
    () =>
      (vehicle: Vehicle, { isSelected }: { isSelected: boolean }) => {
        const driver = vehicle.driver;
        const chip = vehicleStatusChip(t, vehicle.status);
        const detailParts = [
          t(`types.${vehicle.classification.type}`, { defaultValue: vehicle.classification.type }),
          vehicle.classification.year ? String(vehicle.classification.year) : '',
          getVehicleBrandLabel(vehicle.classification.brand, t),
        ].filter(Boolean);
        const detailLine = detailParts.join(' · ');
        const driverLine = driver ? `${driver.name} · ${driver.phone}` : t('selector.no_driver');

        return (
          <div dir={dir} className="flex items-start gap-2 w-full group min-w-0">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-gray-50 p-1.5">
              <VehicleDynamicIcon
                vehicle={vehicle.classification.type}
                size="small"
                className="size-5 text-gray-800"
              />
            </div>
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="min-w-0 truncate text-sm font-medium text-gray-900">
                  {formatPlateForDisplay(vehicle.registration.plate)}
                </span>
                <span className="truncate text-xs text-gray-500">{detailLine}</span>
                <div
                  className={cn(
                    'flex min-w-0 items-center gap-2 text-xs',
                    driver ? 'text-gray-600' : 'text-red-500',
                  )}>
                  <span
                    className={cn(
                      'flex size-6 shrink-0 items-center justify-center rounded-full',
                      driver ? 'bg-gray-100' : 'bg-red-50',
                    )}>
                    <UserIcon
                      className={cn('size-3.5', driver ? 'text-gray-600' : 'text-red-500')}
                      size="small"
                    />
                  </span>
                  <span className="truncate">{driverLine}</span>
                </div>
              </div>
              <AvailabilityChip label={chip.label} tone={chip.tone} />
            </div>
            {mode === 'single' && <SelectRowCheck isSelected={isSelected} />}
          </div>
        );
      },
    [t, mode, dir],
  );

  const renderVehicleTrigger = useMemo(
    () => (selected: Vehicle | Vehicle[] | null) => {
      if (!selected || Array.isArray(selected)) return null;
      const vehicle = selected;
      const driver = vehicle.driver;
      const chip = vehicleStatusChip(t, vehicle.status);
      const typeLabel = t(`types.${vehicle.classification.type}`, {
        defaultValue: vehicle.classification.type,
      });

      return (
        <div dir={dir} className="flex min-w-0 flex-1 items-center gap-2">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-gray-50 p-1">
            <VehicleDynamicIcon
              vehicle={vehicle.classification.type}
              size="small"
              className="size-4 text-gray-800"
            />
          </div>
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="min-w-0 truncate text-sm font-medium text-gray-900">
                {formatPlateForDisplay(vehicle.registration.plate)}
              </span>
              <span className="truncate text-xs text-gray-500">
                {typeLabel}
                {driver ? ` · ${driver.name}` : ` · ${t('selector.no_driver')}`}
              </span>
            </div>
            <AvailabilityChip label={chip.label} tone={chip.tone} />
          </div>
        </div>
      );
    },
    [t, dir],
  );

  return (
    <div className="space-y-3">
      <SelectAsync<Vehicle>
        value={selectedVehicle}
        listQuery={vehicleListQuery}
        singleQuery={vehicleSingleQuery}
        onChangeSelect={onVehicleSelected}
        getItemId={v => v._id}
        placeholder={placeholder || t('selector.placeholder')}
        searchPlaceholder={t('selector.search_placeholder')}
        emptyMessage={t('selector.no_vehicles')}
        loadingMessage={t('c-main:async_operations.loading')}
        createNewLabel={t('c-main:crud_buttons.create_new')}
        onCreateNew={openCreateModal}
        allowCreateNew={allowCreateNew}
        allowEdit={false}
        mode={mode}
        disabled={disabled}
        label={label}
        required={required}
        renderOption={renderVehicleRow}
        renderTrigger={mode === 'single' ? renderVehicleTrigger : undefined}
      />
      {allowCreateNew && (
        <VehicleEditModal
          isOpen={isCreateModalOpen}
          onClose={closeModal}
          onVehicleSaved={handleVehicleSave}
        />
      )}
    </div>
  );
});
